import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, ChevronLeft, ChevronRight, Users, Heart, Thermometer, Activity, Baby, User, AlertCircle, BarChart3, MapPin, Cross, Tent, Plus, Trash2, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Quadrant layout for bubble map
const QUADRANT_MAP = {
  // Right Top (NE)
  'Kurumankottai': { qx: 1, qy: -1, ox: 0.6, oy: 0.25 },
  'Kandrakottai': { qx: 1, qy: -1, ox: 0.35, oy: 0.55 },
  'Kallapattu': { qx: 1, qy: -1, ox: 0.7, oy: 0.7 },
  'Kappiampuliyur': { qx: 1, qy: -1, ox: 0.5, oy: 0.4 },
  'Melpathi': { qx: 1, qy: -1, ox: 0.85, oy: 0.5 },
  // Left Top (NW)
  'Anichampalayam': { qx: -1, qy: -1, ox: 0.4, oy: 0.3 },
  'Maharajapuram': { qx: -1, qy: -1, ox: 0.7, oy: 0.55 },
  'Poyyapakkam': { qx: -1, qy: -1, ox: 0.55, oy: 0.7 },
  'Madhirimangalam': { qx: -1, qy: -1, ox: 0.3, oy: 0.6 },
  'Kuchipalayam': { qx: -1, qy: -1, ox: 0.65, oy: 0.35 },
  // Left Bottom (SW)
  'Panchamadevi Agaram': { qx: -1, qy: 1, ox: 0.3, oy: 0.2 },
  'Nanattapalayam': { qx: -1, qy: 1, ox: 0.55, oy: 0.35 },
  'Koliyanur': { qx: -1, qy: 1, ox: 0.7, oy: 0.15 },
  'Salai Agaram': { qx: -1, qy: 1, ox: 0.4, oy: 0.5 },
  'Thodarthanur': { qx: -1, qy: 1, ox: 0.65, oy: 0.65 },
  'Kamban Nagar': { qx: -1, qy: 1, ox: 0.25, oy: 0.7 },
  'Ragavanpettai': { qx: -1, qy: 1, ox: 0.5, oy: 0.8 },
  'Chinnakuchipalayam': { qx: -1, qy: 1, ox: 0.8, oy: 0.45 },
  'Villupuram': { qx: -1, qy: 1, ox: 0.35, oy: 0.9 },
  // Right Bottom (SE)
  'Pananguppam': { qx: 1, qy: 1, ox: 0.3, oy: 0.2 },
  'Nalarasanpettai': { qx: 1, qy: 1, ox: 0.55, oy: 0.35 },
  'Nallarasanpettai': { qx: 1, qy: 1, ox: 0.55, oy: 0.35 },
  'Sagadevanpettai': { qx: 1, qy: 1, ox: 0.7, oy: 0.5 },
  'Ramaiyanpalayam': { qx: 1, qy: 1, ox: 0.4, oy: 0.55 },
  'Valavanur': { qx: 1, qy: 1, ox: 0.6, oy: 0.7 },
  'Panruti': { qx: 1, qy: 1, ox: 0.8, oy: 0.8 },
  'Ottaripalayam': { qx: 1, qy: 1, ox: 0.35, oy: 0.8 },
  'Salaiyampalayam': { qx: 1, qy: 1, ox: 0.55, oy: 0.9 },
  'Panampattu': { qx: 1, qy: 1, ox: 0.75, oy: 0.25 },
};

const OUTSIDE_PLACES = ['Pondy', 'Chennai', 'P. Villianur', 'Arasamangalam'];

const QUADRANT_COLORS = {
  'NE': '#2563eb',  // Blue - Right Top
  'NW': '#059669',  // Green - Left Top
  'SW': '#d97706',  // Amber - Left Bottom
  'SE': '#dc2626',  // Red - Right Bottom
  'OUT': '#7c3aed', // Purple - Outside
};

const QUADRANT_LABELS = {
  'NE': 'Right Top',
  'NW': 'Left Top',
  'SW': 'Left Bottom',
  'SE': 'Right Bottom',
};

const BubbleMap = ({ flowData }) => {
  const [hoveredBubble, setHoveredBubble] = useState(null);
  if (!flowData || !flowData.flow) return null;

  const flowMap = {};
  flowData.flow.forEach(f => { flowMap[f.place] = f.count; });
  const maxCount = Math.max(...flowData.flow.map(f => f.count), 1);

  const W = 800, H = 600;
  const CX = W / 2, CY = H / 2;
  const QPAD = 30;

  const getRadius = (count) => {
    if (!count) return 6;
    return Math.max(10, Math.min(42, 10 + (count / maxCount) * 32));
  };

  const getBubblePos = (name) => {
    // Exact match first
    let mapped = QUADRANT_MAP[name];
    if (!mapped) {
      // Case-insensitive match
      const lowerName = name.toLowerCase();
      const key = Object.keys(QUADRANT_MAP).find(k => k.toLowerCase() === lowerName);
      if (key) mapped = QUADRANT_MAP[key];
    }
    if (!mapped) {
      // Partial match (e.g., "Salai" matches "Salai Agaram")
      const lowerName = name.toLowerCase();
      const key = Object.keys(QUADRANT_MAP).find(k => k.toLowerCase().startsWith(lowerName) || lowerName.startsWith(k.toLowerCase()));
      if (key) mapped = QUADRANT_MAP[key];
    }
    if (mapped) {
      const qx = mapped.qx, qy = mapped.qy;
      const halfW = (W / 2) - QPAD - 50;
      const halfH = (H / 2) - QPAD - 30;
      const x = CX + qx * (QPAD + mapped.ox * halfW);
      const y = CY + qy * (QPAD + mapped.oy * halfH);
      return { x, y, quadrant: qx > 0 ? (qy < 0 ? 'NE' : 'SE') : (qy < 0 ? 'NW' : 'SW') };
    }
    return null;
  };

  const mappedBubbles = [];
  const outsideBubbles = [];
  const unmappedBubbles = [];

  flowData.flow.forEach(f => {
    const isOutside = OUTSIDE_PLACES.some(op => f.place.toLowerCase().includes(op.toLowerCase())) ||
      f.place.toLowerCase() === 'other';
    if (isOutside) {
      outsideBubbles.push(f);
    } else {
      const pos = getBubblePos(f.place);
      if (pos) {
        mappedBubbles.push({ ...f, ...pos, r: getRadius(f.count) });
      } else {
        unmappedBubbles.push(f);
      }
    }
  });

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: '600px' }}>
        <defs>
          <radialGradient id="clinic-glow">
            <stop offset="0%" stopColor="#dc2626" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#dc2626" stopOpacity="0" />
          </radialGradient>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.15" />
          </filter>
        </defs>

        {/* Background quadrant lines */}
        <line x1={CX} y1={QPAD} x2={CX} y2={H - QPAD} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="6 4" />
        <line x1={QPAD} y1={CY} x2={W - QPAD} y2={CY} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="6 4" />

        {/* Quadrant labels */}
        <text x={W - 60} y={24} textAnchor="end" className="fill-blue-400 text-xs font-medium" fontSize="11">NE - Right Top</text>
        <text x={60} y={24} textAnchor="start" className="fill-green-500 text-xs font-medium" fontSize="11">NW - Left Top</text>
        <text x={60} y={H - 12} textAnchor="start" className="fill-amber-500 text-xs font-medium" fontSize="11">SW - Left Bottom</text>
        <text x={W - 60} y={H - 12} textAnchor="end" className="fill-red-500 text-xs font-medium" fontSize="11">SE - Right Bottom</text>

        {/* Clinic center */}
        <circle cx={CX} cy={CY} r={45} fill="url(#clinic-glow)" />
        <circle cx={CX} cy={CY} r={22} fill="#991b1b" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />
        <text x={CX} y={CY - 3} textAnchor="middle" fill="#fff" fontWeight="900" fontSize="9">K.M</text>
        <text x={CX} y={CY + 8} textAnchor="middle" fill="#fecaca" fontSize="6">CLINIC</text>

        {/* Village bubbles */}
        {mappedBubbles.map((b, i) => {
          const color = QUADRANT_COLORS[b.quadrant];
          const isHovered = hoveredBubble === b.place;
          return (
            <g key={i}
              onMouseEnter={() => setHoveredBubble(b.place)}
              onMouseLeave={() => setHoveredBubble(null)}
              style={{ cursor: 'pointer' }}
            >
              <circle cx={b.x} cy={b.y} r={b.r + (isHovered ? 4 : 0)}
                fill={color} fillOpacity={isHovered ? 0.9 : 0.7}
                stroke={isHovered ? '#111' : '#fff'} strokeWidth={isHovered ? 2 : 1.5}
                filter="url(#shadow)"
              />
              {/* Count inside bubble */}
              <text x={b.x} y={b.y + 1} textAnchor="middle" dominantBaseline="middle"
                fill="#fff" fontWeight="800" fontSize={b.r > 18 ? '12' : '9'}>
                {b.count}
              </text>
              {/* Name label */}
              <text x={b.x} y={b.y + b.r + 12} textAnchor="middle"
                fill="#374151" fontWeight={isHovered ? '700' : '500'}
                fontSize={isHovered ? '11' : '9'}>
                {b.place.length > 14 ? b.place.substring(0, 12) + '..' : b.place}
              </text>
            </g>
          );
        })}

        {/* Hover tooltip */}
        {hoveredBubble && (() => {
          const b = mappedBubbles.find(bb => bb.place === hoveredBubble);
          if (!b) return null;
          const tw = Math.max(b.place.length * 7, 100);
          const tx = Math.min(Math.max(b.x - tw / 2, 5), W - tw - 5);
          const ty = b.y - b.r - 38;
          return (
            <g>
              <rect x={tx} y={ty} width={tw} height={30} rx={6} fill="#1f2937" fillOpacity="0.92" />
              <text x={tx + tw / 2} y={ty + 12} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700">{b.place}</text>
              <text x={tx + tw / 2} y={ty + 24} textAnchor="middle" fill="#93c5fd" fontSize="10">{b.count} patient{b.count !== 1 ? 's' : ''}</text>
            </g>
          );
        })()}
      </svg>

      {/* Outside places (Pondy, Chennai, etc.) */}
      {(outsideBubbles.length > 0 || unmappedBubbles.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-2 justify-center">
          {outsideBubbles.map((b, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-purple-50 border border-purple-200 rounded-full px-3 py-1">
              <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-black">{b.count}</div>
              <span className="text-xs font-bold text-purple-800">{b.place}</span>
            </div>
          ))}
          {unmappedBubbles.map((b, i) => (
            <div key={`u-${i}`} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
              <div className="w-6 h-6 rounded-full bg-gray-500 text-white flex items-center justify-center text-xs font-black">{b.count}</div>
              <span className="text-xs font-bold text-gray-700">{b.place}</span>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-500 justify-center">
        {Object.entries(QUADRANT_LABELS).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: QUADRANT_COLORS[k] }}></span>
            {v}
          </span>
        ))}
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-purple-500 inline-block"></span>
          Outside
        </span>
        <span className="text-gray-400 ml-2">Bubble size = patient count</span>
      </div>
    </div>
  );
};

const Reports = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('demographics');
  
  // Demographics state
  const [demoMonth, setDemoMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [demographics, setDemographics] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);

  // Census state
  const [censusPeriod, setCensusPeriod] = useState('monthly');
  const [censusYear, setCensusYear] = useState(new Date().getFullYear());
  const [censusData, setCensusData] = useState(null);
  const [censusLoading, setCensusLoading] = useState(false);

  // Patient Flow state
  const [flowPeriod, setFlowPeriod] = useState('month');
  const [flowMonth, setFlowMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [flowDate, setFlowDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [flowData, setFlowData] = useState(null);
  const [flowLoading, setFlowLoading] = useState(false);

  // Camp tracker state
  const [camps, setCamps] = useState([]);
  const [campLoading, setCampLoading] = useState(false);
  const [showAddCamp, setShowAddCamp] = useState(false);
  const [campPlace, setCampPlace] = useState('');
  const [campDate, setCampDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [campNotes, setCampNotes] = useState('');
  const [campEffectiveness, setCampEffectiveness] = useState({});
  const [placesList, setPlacesList] = useState([]);

  const fetchDemographics = useCallback(async (month) => {
    setDemoLoading(true);
    try {
      const response = await axios.get(`${API}/reports/demographics?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDemographics(response.data);
    } catch (error) {
      toast.error('Failed to load demographics');
    } finally {
      setDemoLoading(false);
    }
  }, [token]);

  const fetchCensus = useCallback(async (period, year) => {
    setCensusLoading(true);
    try {
      const response = await axios.get(`${API}/reports/census?period=${period}&year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCensusData(response.data);
    } catch (error) {
      toast.error('Failed to load census data');
    } finally {
      setCensusLoading(false);
    }
  }, [token]);

  const fetchPatientFlow = useCallback(async (period, monthVal, dateVal) => {
    setFlowLoading(true);
    try {
      let url = `${API}/reports/patient-flow?period=${period}`;
      if (period === 'month') url += `&month=${monthVal}`;
      else if (period === 'day') url += `&date_str=${dateVal}`;
      else if (period === 'week') url += `&date_str=${dateVal}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setFlowData(response.data);
    } catch (error) {
      toast.error('Failed to load patient flow data');
    } finally {
      setFlowLoading(false);
    }
  }, [token]);

  const fetchCamps = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/camps`, { headers: { Authorization: `Bearer ${token}` } });
      setCamps(response.data);
      // Auto-fetch effectiveness for each camp
      const effMap = {};
      for (const c of response.data.slice(0, 10)) {
        try {
          const r = await axios.get(`${API}/camps/${c.id}/effectiveness`, { headers: { Authorization: `Bearer ${token}` } });
          effMap[c.id] = r.data;
        } catch { /* skip */ }
      }
      setCampEffectiveness(effMap);
    } catch { /* ignore */ }
  }, [token]);

  const fetchPlacesList = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/places`, { headers: { Authorization: `Bearer ${token}` } });
      setPlacesList(response.data.places || []);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    fetchDemographics(demoMonth);
    fetchCensus(censusPeriod, censusYear);
  }, [fetchDemographics, fetchCensus, demoMonth, censusPeriod, censusYear]);

  useEffect(() => {
    if (activeTab === 'flow') {
      fetchPatientFlow(flowPeriod, flowMonth, flowDate);
      fetchCamps();
      fetchPlacesList();
    }
  }, [activeTab, flowPeriod, flowMonth, flowDate, fetchPatientFlow, fetchCamps, fetchPlacesList]);

  const COLORS = ['#991b1b', '#be185d', '#b45309', '#059669', '#2563eb', '#7c3aed', '#dc2626', '#0891b2'];

  const navigateMonth = (dir) => {
    const [y, m] = demoMonth.split('-').map(Number);
    let nm, ny;
    if (dir === -1) {
      nm = m === 1 ? 12 : m - 1;
      ny = m === 1 ? y - 1 : y;
    } else {
      nm = m === 12 ? 1 : m + 1;
      ny = m === 12 ? y + 1 : y;
    }
    const newMonth = `${ny}-${String(nm).padStart(2, '0')}`;
    setDemoMonth(newMonth);
  };

  const navigateFlowMonth = (dir) => {
    const [y, m] = flowMonth.split('-').map(Number);
    let nm, ny;
    if (dir === -1) {
      nm = m === 1 ? 12 : m - 1;
      ny = m === 1 ? y - 1 : y;
    } else {
      nm = m === 12 ? 1 : m + 1;
      ny = m === 12 ? y + 1 : y;
    }
    setFlowMonth(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  const navigateFlowDate = (days) => {
    const d = new Date(flowDate);
    d.setDate(d.getDate() + days);
    setFlowDate(d.toISOString().split('T')[0]);
  };

  const addCamp = async () => {
    if (!campPlace || !campDate) { toast.error('Place and date required'); return; }
    setCampLoading(true);
    try {
      await axios.post(`${API}/camps`, { place: campPlace, camp_date: campDate, notes: campNotes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Camp logged!');
      setCampPlace('');
      setCampNotes('');
      setShowAddCamp(false);
      fetchCamps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log camp');
    } finally { setCampLoading(false); }
  };

  const deleteCamp = async (campId) => {
    try {
      await axios.delete(`${API}/camps/${campId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Camp deleted');
      fetchCamps();
    } catch { toast.error('Failed to delete camp'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-900 to-red-800 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-red-700/30 p-2">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">Reports & Analytics</h1>
                <p className="text-xs text-red-200">K.M Multi-Speciality Clinic</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white p-1 rounded-lg shadow-sm border">
          {[
            { key: 'demographics', label: 'Demographics', icon: Users },
            { key: 'census', label: 'Census Charts', icon: BarChart3 },
            { key: 'flow', label: 'Patient Flow', icon: MapPin },
            { key: 'backup', label: 'Backup', icon: Calendar }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.key ? 'bg-red-800 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Demographics Tab */}
        {activeTab === 'demographics' && (
          <div>
            {/* Month Navigation */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <input
                type="month"
                value={demoMonth}
                onChange={(e) => setDemoMonth(e.target.value)}
                className="w-44 h-10 text-center font-bold border rounded-lg px-3"
                data-testid="demo-month-input"
              />
              <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {demoLoading && <div className="text-center py-8 text-gray-500">Loading demographics...</div>}

            {!demoLoading && demographics && (
              <div className="space-y-6">
                {/* Total Banner */}
                <div className="bg-gradient-to-r from-red-900 to-red-800 rounded-xl p-5 text-white text-center shadow-lg" data-testid="demo-total-banner">
                  <div className="text-xs font-medium opacity-80 uppercase tracking-wider">{demographics.month_name}</div>
                  <div className="text-5xl font-black mt-1">{demographics.total}</div>
                  <div className="text-sm opacity-80">Total Patients</div>
                </div>

                {/* Gender & Age Breakdown */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Card className="p-4 text-center border-2 border-blue-200 bg-blue-50">
                    <User className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                    <div className="text-3xl font-black text-blue-800">{demographics.male}</div>
                    <div className="text-sm font-medium text-blue-600">Male</div>
                  </Card>
                  <Card className="p-4 text-center border-2 border-pink-200 bg-pink-50">
                    <User className="h-8 w-8 mx-auto text-pink-600 mb-2" />
                    <div className="text-3xl font-black text-pink-800">{demographics.female}</div>
                    <div className="text-sm font-medium text-pink-600">Female</div>
                  </Card>
                  <Card className="p-4 text-center border-2 border-red-200 bg-red-50">
                    <AlertCircle className="h-8 w-8 mx-auto text-red-600 mb-2" />
                    <div className="text-3xl font-black text-red-800">{demographics.er}</div>
                    <div className="text-sm font-medium text-red-600">ER Patients</div>
                  </Card>
                  <Card className="p-4 text-center border-2 border-amber-200 bg-amber-50">
                    <Activity className="h-8 w-8 mx-auto text-amber-600 mb-2" />
                    <div className="text-3xl font-black text-amber-800">{demographics.diabetic}</div>
                    <div className="text-sm font-medium text-amber-600">Diabetic</div>
                  </Card>
                  <Card className="p-4 text-center border-2 border-orange-200 bg-orange-50">
                    <Thermometer className="h-8 w-8 mx-auto text-orange-600 mb-2" />
                    <div className="text-3xl font-black text-orange-800">{demographics.fever}</div>
                    <div className="text-sm font-medium text-orange-600">Fever</div>
                  </Card>
                  <Card className="p-4 text-center border-2 border-purple-200 bg-purple-50">
                    <Heart className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                    <div className="text-3xl font-black text-purple-800">{demographics.htn}</div>
                    <div className="text-sm font-medium text-purple-600">Hypertension</div>
                  </Card>
                </div>

                {/* Age Group Breakdown */}
                <Card className="p-5 border">
                  <h3 className="font-bold text-gray-800 mb-4">Age Group Distribution</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <Baby className="h-6 w-6 mx-auto text-green-600 mb-1" />
                      <div className="text-2xl font-black text-green-800">{demographics.under5}</div>
                      <div className="text-xs text-green-600">Under 5</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <Users className="h-6 w-6 mx-auto text-blue-600 mb-1" />
                      <div className="text-2xl font-black text-blue-800">{demographics.children_5_12}</div>
                      <div className="text-xs text-blue-600">Children (5-12)</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <User className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                      <div className="text-2xl font-black text-gray-800">{demographics.adults}</div>
                      <div className="text-xs text-gray-600">Adults (12+)</div>
                    </div>
                  </div>
                </Card>

                {/* Pie Charts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Gender Pie */}
                  <Card className="p-5 border">
                    <h3 className="font-bold text-gray-800 mb-3">Gender Distribution</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Male', value: demographics.male },
                            { name: 'Female', value: demographics.female },
                            ...(demographics.other_sex > 0 ? [{ name: 'Other', value: demographics.other_sex }] : [])
                          ].filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          <Cell fill="#2563eb" />
                          <Cell fill="#ec4899" />
                          <Cell fill="#6b7280" />
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>

                  {/* Conditions Pie */}
                  <Card className="p-5 border">
                    <h3 className="font-bold text-gray-800 mb-3">Medical Conditions</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'ER', value: demographics.er },
                            { name: 'Diabetic', value: demographics.diabetic },
                            { name: 'Fever', value: demographics.fever },
                            { name: 'HTN', value: demographics.htn },
                          ].filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {[0, 1, 2, 3].map(i => (
                            <Cell key={i} fill={COLORS[i]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Census Charts Tab */}
        {activeTab === 'census' && (
          <div>
            {/* Period Selection */}
            <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
              <div className="flex gap-1 bg-white p-1 rounded-lg border">
                {['weekly', 'monthly', 'yearly'].map(p => (
                  <button
                    key={p}
                    onClick={() => setCensusPeriod(p)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      censusPeriod === p ? 'bg-red-800 text-white shadow' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`census-${p}`}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
              {censusPeriod === 'monthly' && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCensusYear(censusYear - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-bold text-red-900 text-lg">{censusYear}</span>
                  <Button variant="outline" size="sm" onClick={() => setCensusYear(censusYear + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {censusLoading && <div className="text-center py-8 text-gray-500">Loading census data...</div>}

            {!censusLoading && censusData && (
              <div className="space-y-6">
                {/* Total for Period */}
                <div className="bg-gradient-to-r from-red-900 to-red-800 rounded-xl p-4 text-white text-center shadow-lg" data-testid="census-total-banner">
                  <div className="text-xs font-medium opacity-80 uppercase tracking-wider">
                    {censusPeriod === 'weekly' ? 'Last 12 Weeks' : censusPeriod === 'monthly' ? `Year ${censusYear}` : 'Last 5 Years'}
                  </div>
                  <div className="text-4xl font-black mt-1">
                    {censusData.data.reduce((sum, d) => sum + d.count, 0)}
                  </div>
                  <div className="text-sm opacity-80">Total Patients</div>
                </div>

                {/* Bar Chart */}
                <Card className="p-5 border">
                  <h3 className="font-bold text-gray-800 mb-4">
                    {censusPeriod === 'weekly' ? 'Weekly' : censusPeriod === 'monthly' ? 'Monthly' : 'Yearly'} Patient Census
                  </h3>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={censusData.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        formatter={(value) => [`${value} patients`, 'Count']}
                      />
                      <Bar dataKey="count" fill="#991b1b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                {/* Line Chart */}
                <Card className="p-5 border">
                  <h3 className="font-bold text-gray-800 mb-4">Trend Line</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={censusData.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        formatter={(value) => [`${value} patients`, 'Count']}
                      />
                      <Line type="monotone" dataKey="count" stroke="#991b1b" strokeWidth={2} dot={{ fill: '#991b1b', r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

                {/* Data Table */}
                <Card className="p-5 border">
                  <h3 className="font-bold text-gray-800 mb-3">Detailed Breakdown</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 font-semibold text-gray-600">Period</th>
                          <th className="text-right p-2 font-semibold text-gray-600">Patients</th>
                        </tr>
                      </thead>
                      <tbody>
                        {censusData.data.map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-2 font-medium">{item.label}</td>
                            <td className="p-2 text-right font-bold text-red-800">{item.count}</td>
                          </tr>
                        ))}
                        <tr className="bg-red-50 font-bold">
                          <td className="p-2">Total</td>
                          <td className="p-2 text-right text-red-900">{censusData.data.reduce((s, d) => s + d.count, 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Patient Flow Tab */}
        {activeTab === 'flow' && (
          <div>
            {/* Period selector + date navigation */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
              <div className="flex gap-1 bg-white p-1 rounded-lg border">
                {['day', 'week', 'month'].map(p => (
                  <button
                    key={p}
                    onClick={() => setFlowPeriod(p)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      flowPeriod === p ? 'bg-red-800 text-white shadow' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`flow-period-${p}`}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
              {flowPeriod === 'month' && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigateFlowMonth(-1)}><ChevronLeft className="h-4 w-4" /></Button>
                  <input type="month" value={flowMonth} onChange={(e) => setFlowMonth(e.target.value)}
                    className="w-44 h-10 text-center font-bold border rounded-lg px-3" data-testid="flow-month-input" />
                  <Button variant="outline" size="sm" onClick={() => navigateFlowMonth(1)}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              )}
              {(flowPeriod === 'day' || flowPeriod === 'week') && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigateFlowDate(flowPeriod === 'week' ? -7 : -1)}><ChevronLeft className="h-4 w-4" /></Button>
                  <input type="date" value={flowDate} onChange={(e) => setFlowDate(e.target.value)}
                    className="w-44 h-10 text-center font-bold border rounded-lg px-3" data-testid="flow-date-input" />
                  <Button variant="outline" size="sm" onClick={() => navigateFlowDate(flowPeriod === 'week' ? 7 : 1)}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              )}
            </div>

            {flowLoading && <div className="text-center py-8 text-gray-500">Loading patient flow...</div>}

            {!flowLoading && flowData && (
              <div className="space-y-6">
                {/* Summary Banner */}
                <div className="bg-gradient-to-r from-red-900 to-red-800 rounded-xl p-5 text-white shadow-lg" data-testid="flow-total-banner">
                  <div className="text-xs font-medium opacity-80 uppercase tracking-wider">{flowData.label} - Patient Flow</div>
                  <div className="flex items-center justify-center gap-8 mt-2">
                    <div className="text-center">
                      <div className="text-4xl font-black">{flowData.total}</div>
                      <div className="text-sm opacity-80">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-black text-green-300">{flowData.mapped}</div>
                      <div className="text-sm opacity-80">Mapped</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-black text-amber-300">{flowData.unmapped}</div>
                      <div className="text-sm opacity-80">Unmapped</div>
                    </div>
                  </div>
                </div>

                {/* Bubble Map */}
                {flowData.flow.length > 0 && (
                  <Card className="p-5 border" data-testid="flow-bubble-map">
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-red-700" />
                      Patient Flow Map — Clinic at Centre
                    </h3>
                    <BubbleMap flowData={flowData} />
                  </Card>
                )}

                {/* Flow Table */}
                <Card className="p-5 border">
                  <h3 className="font-bold text-gray-800 mb-3">Area-wise Breakdown</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 font-semibold text-gray-600">#</th>
                          <th className="text-left p-2 font-semibold text-gray-600">Place</th>
                          <th className="text-right p-2 font-semibold text-gray-600">Patients</th>
                          <th className="text-right p-2 font-semibold text-gray-600">% Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flowData.flow.map((item, idx) => (
                          <tr key={idx} className={`border-b hover:bg-gray-50 ${idx < 3 ? 'bg-green-50' : ''}`}>
                            <td className="p-2 text-gray-500">{idx + 1}</td>
                            <td className="p-2 font-medium flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              {item.place}
                            </td>
                            <td className="p-2 text-right font-bold text-red-800">{item.count}</td>
                            <td className="p-2 text-right text-gray-600">
                              {flowData.total > 0 ? ((item.count / flowData.total) * 100).toFixed(1) : 0}%
                            </td>
                          </tr>
                        ))}
                        {flowData.unmapped > 0 && (
                          <tr className="border-b bg-amber-50">
                            <td className="p-2 text-gray-400">-</td>
                            <td className="p-2 font-medium text-amber-700 italic">Unmapped (no place)</td>
                            <td className="p-2 text-right font-bold text-amber-700">{flowData.unmapped}</td>
                            <td className="p-2 text-right text-amber-600">
                              {flowData.total > 0 ? ((flowData.unmapped / flowData.total) * 100).toFixed(1) : 0}%
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {flowData.flow.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <MapPin className="h-12 w-12 mx-auto mb-3 opacity-40" />
                    <p className="font-medium">No place data for this period</p>
                    <p className="text-sm">Start entering patient places in the Staff dashboard</p>
                  </div>
                )}
              </div>
            )}

            {/* Camp Effectiveness Tracker */}
            <Card className="p-5 border mt-6" data-testid="camp-tracker-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <Tent className="h-5 w-5 text-green-700" />
                  Camp Effectiveness Tracker
                </h3>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => setShowAddCamp(!showAddCamp)} data-testid="add-camp-button">
                  <Plus className="h-4 w-4 mr-1" /> Log Camp
                </Button>
              </div>

              {/* Add Camp Form */}
              {showAddCamp && (
                <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200 space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs font-medium text-green-700 block mb-1">Place</label>
                      <select value={campPlace} onChange={(e) => setCampPlace(e.target.value)}
                        className="w-full h-9 rounded-md border px-2 text-sm" data-testid="camp-place-select">
                        <option value="">Select village...</option>
                        {placesList.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-green-700 block mb-1">Camp Date</label>
                      <input type="date" value={campDate} onChange={(e) => setCampDate(e.target.value)}
                        className="w-full h-9 rounded-md border px-2 text-sm" data-testid="camp-date-input" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-green-700 block mb-1">Notes</label>
                      <input type="text" value={campNotes} onChange={(e) => setCampNotes(e.target.value)}
                        placeholder="Optional notes" className="w-full h-9 rounded-md border px-2 text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={addCamp} disabled={campLoading} data-testid="save-camp-button">
                      {campLoading ? 'Saving...' : 'Save Camp'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAddCamp(false)}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* Camp List with Effectiveness */}
              {camps.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No camps logged yet. Click "Log Camp" to start tracking.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {camps.map(camp => {
                    const eff = campEffectiveness[camp.id];
                    return (
                      <div key={camp.id} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow" data-testid={`camp-entry-${camp.id}`}>
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <Tent className="h-5 w-5 text-green-700" />
                          </div>
                          <div>
                            <div className="font-bold text-gray-800 text-sm">{camp.place}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(camp.camp_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              {camp.notes && <span className="ml-1 text-gray-400">- {camp.notes}</span>}
                            </div>
                          </div>
                        </div>
                        {/* Effectiveness */}
                        {eff ? (
                          <div className="flex items-center gap-3">
                            <div className="text-center">
                              <div className="text-xs text-gray-500">Before</div>
                              <div className="text-sm font-bold text-gray-700">{eff.before.count}</div>
                            </div>
                            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                              eff.effective ? 'bg-green-100 text-green-700' : eff.change === 0 ? 'bg-gray-100 text-gray-500' : 'bg-red-100 text-red-700'
                            }`}>
                              {eff.effective ? <TrendingUp className="h-3 w-3" /> : eff.change < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                              {eff.change > 0 ? '+' : ''}{eff.change}
                              {eff.before.count > 0 && <span className="ml-0.5">({eff.change_pct > 0 ? '+' : ''}{eff.change_pct}%)</span>}
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-gray-500">After</div>
                              <div className="text-sm font-bold text-gray-700">{eff.after.count}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Loading...</span>
                        )}
                        <Button variant="ghost" size="sm" className="ml-2 text-red-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => deleteCamp(camp.id)} data-testid={`delete-camp-${camp.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-3 text-center">Compares patient count from the area: 30 days before vs 30 days after camp date</p>
            </Card>
          </div>
        )}

        {/* Backup Tab — monthly Excel export */}
        {activeTab === 'backup' && <BackupTab />}
      </div>
    </div>
  );
};

// ---- Backup Tab Component ----
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BackupTab = () => {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const tokenStr = (typeof window !== 'undefined' && window.localStorage.getItem('token')) || '';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/backup/months`, {
          headers: { Authorization: `Bearer ${tokenStr}` }
        });
        setMonths(r.data?.months || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load months');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tokenStr]);

  const downloadBackup = async (month) => {
    setDownloading(month);
    try {
      const r = await axios.get(`${API_URL}/backup/monthly.xlsx`, {
        params: { month },
        headers: { Authorization: `Bearer ${tokenStr}` },
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clinic_backup_${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`✓ ${month} backup downloaded`);
    } catch (e) {
      toast.error('Failed to download backup');
    } finally {
      setDownloading(null);
    }
  };

  // Build a "last 12 months" list so a user can also grab months that have no data yet
  const allMonths = (() => {
    const result = [];
    const t = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(t.getFullYear(), t.getMonth() - i, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      result.push(m);
    }
    return result;
  })();

  const monthMap = Object.fromEntries(months.map(m => [m.month, m.patients]));

  return (
    <div className="space-y-4" data-testid="backup-tab">
      {/* Header card */}
      <Card className="p-5 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-emerald-100 p-3">
            <Calendar className="h-6 w-6 text-emerald-700" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-emerald-900">Monthly Data Backup</h2>
            <p className="text-sm text-emerald-800/80 mt-1">
              Download a 4-sheet Excel file containing every patient registered in the month — demographics, place, arrival source — plus daily revenue, monthly P&amp;L and the doctor&apos;s punch log.
            </p>
            <ul className="text-xs text-emerald-700 mt-2 space-y-0.5">
              <li>• <b>Patients</b> sheet — name, age, sex, place, phone, source, flags, cabin/done times, consult fee</li>
              <li>• <b>Revenue</b> sheet — daily consult, nursing, reconciled grand totals</li>
              <li>• <b>P&amp;L</b> sheet — monthly summary, source mix, top places</li>
              <li>• <b>Doctor Punch Log</b> sheet — daily arrival vs target, status</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Months list */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-800">Choose a month</h3>
          <span className="text-xs text-slate-400">{loading ? 'Loading…' : `${months.length} month(s) with data`}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {allMonths.map((m) => {
            const count = monthMap[m] || 0;
            const date = new Date(m + '-01');
            const label = date.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
            const isCurrent = m === allMonths[0];
            return (
              <div
                key={m}
                className={`flex items-center justify-between rounded-lg border p-3 hover:bg-slate-50 transition ${count > 0 ? 'border-slate-200' : 'border-dashed border-slate-200 opacity-60'}`}
                data-testid={`backup-row-${m}`}
              >
                <div>
                  <div className="font-bold text-slate-800 flex items-center gap-2">
                    {label}
                    {isCurrent && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">CURRENT</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {count > 0 ? `${count} patient${count === 1 ? '' : 's'} recorded` : 'no patients yet'}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => downloadBackup(m)}
                  disabled={downloading === m}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid={`backup-download-${m}`}
                >
                  {downloading === m ? 'Generating…' : 'Download .xlsx'}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-400 mt-4">
          Tip: bookmark the URL — backups can be regenerated any time, the data is always pulled fresh from the database.
        </p>
      </Card>
    </div>
  );
};

export default Reports;
